import { useTranslation } from '../presentation/common/useTranslation.js';

import './HeroRank.css';

const RANKS = ['F', 'E', 'D', 'C', 'B', 'A', 'S'];

// Small optical corrections balance open letters inside the diamond.
const LETTER_OFFSETS = { F: [1, 1], E: [.5, 0], D: [.5, 0], C: [.5, 0], B: [.5, 0], A: [0, -.5], S: [0, 0] };
const LETTER_PATHS = {
  F: 'M7-11H-7V11M-7-1H5',
  E: 'M7-11H-7V11H7M-7-1H5',
  D: 'M-8-11H-2Q8-11 8 0Q8 11-2 11H-8Z',
  C: 'M8-8Q5-11 0-11Q-8-11-8 0Q-8 11 0 11Q5 11 8 8',
  B: 'M-8 0H1Q8 0 8 5.5Q8 11 1 11H-8V-11H0Q7-11 7-5.5Q7 0 1 0',
  A: 'M-8 11 0-11 8 11M-5 3H5',
  S: 'M8-8Q5-11 0-11Q-8-11-8-5.5Q-8-1 0 0Q8 1 8 5.5Q8 11 0 11Q-5 11-8 8',
};

function RankEmblem({ value, label }) {
  const tier = RANKS.indexOf(value);
  const [offsetX, offsetY] = LETTER_OFFSETS[value] || [0, 0];
  return <svg className={`hero-rank-emblem hero-rank-tier-${Math.max(0, tier)}`} viewBox="0 0 120 120" role="img" aria-label={`${label} ${value}`}>
    {tier >= 3 && <g className="hero-rank-wings" fill="currentColor" fillOpacity=".18" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
      {[false, true].map(mirror => <g key={String(mirror)} transform={mirror ? 'translate(120 0) scale(-1 1)' : undefined}>
        <path d={tier >= 5 ? 'M42 52 5 20 10 44 19 62 40 76Z' : tier >= 4 ? 'M42 52 9 32 15 53 25 65 40 73Z' : 'M42 55 19 45 24 59 40 70Z'} />
        <path d={tier >= 5 ? 'M10 44 39 62M19 62 39 69M8 32 40 56' : tier >= 4 ? 'M15 53 39 63M25 65 40 69' : 'M24 59 39 65'} fill="none" />
        {tier >= 6 && <path d="M22 70 29 82 46 89 41 80Z" />}
      </g>)}
    </g>}
    {/* The opaque outer frame cleanly hides wing roots behind the badge. */}
    {tier >= 2 && <path className="hero-rank-frame hero-rank-outer-frame" d="M60 22 91 58 60 94 29 58Z" />}
    <path className="hero-rank-frame" d="M60 28 86 58 60 88 34 58Z" />
    {tier >= 1 && <path className="hero-rank-ornament" d="M60 34 80 58 60 82 40 58Z" />}
    {tier >= 2 && <path fill="currentColor" d="m60 89 3 4-3 4-3-4Z" />}
    {/* The crown's lower edge follows both upper diamond edges exactly. */}
    {tier >= 5 && <path className="hero-rank-crown" d="M44.5 40 42 18 53 24 60 12 67 24 78 18 75.5 40 60 22Z" />}
    {tier === 6 && <g className="hero-rank-ornament"><path d="M37 91Q41 101 50 103M40 96l-4 1M45 101l-2 4M83 91Q79 101 70 103M80 96l4 1M75 101l2 4" /><path d="m60 101 2.2 4.5 5 .7-3.6 3.5.9 5-4.5-2.4-4.5 2.4.9-5-3.6-3.5 5-.7Z" fill="currentColor" /></g>}
    <path className="hero-rank-letter" transform={`translate(${60 + offsetX} ${58 + offsetY}) scale(.72)`} d={LETTER_PATHS[value] || 'M-7 0H7'} />
  </svg>;
}

/** Shared rank value and label for cards, badges and inline text. */
export default function HeroRank({ rank, variant = 'inline', showLabel = false, className = '' }) {
  const { t } = useTranslation();
  const value = String(rank || '—').trim().toUpperCase();
  const label = t('dashboard_v2.rank');
  const emblem = <RankEmblem value={value} label={label} />;
  if (variant === 'identity') return <div className={`dashboard-identity-item hero-rank-identity ${className}`}><small>{label}</small><strong>{emblem}</strong></div>;
  return <span className={`hero-rank hero-rank-${variant} ${className}`}>{showLabel && <span aria-hidden="true">{label} </span>}{emblem}</span>;
}
